import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { useLocalClock } from "@/hooks/useLocalClock";
import { useUserProfile } from "@/hooks/useUserProfile";
import { getStockfishPreferredMove } from "@/lib/unified-engine";
import { toast } from "@/hooks/use-toast";
import { BotChatPanel } from "@/components/BotChatPanel";
import { PlayerInfoCard } from "@/components/PlayerInfoCard";
import { GameControls } from "@/components/GameControls";
import { MoveHistory } from "@/components/MoveHistory";
import { NativeChessBoard } from "@/components/NativeChessBoard";
import { 
  generateBotMessage, 
  shouldBotReact, 
  type ChatMessage, 
  type ChatTone, 
  type BotChatEvent 
} from "@/lib/bot-chat";
import { 
  createInitialGameState, 
  getAllLegalMoves, 
  validateMove, 
  getGameStatus,
  pieceToChar,
  type GameState, 
  type Position,
  type PieceType,
  type Piece
} from "@/lib/chess-engine";

interface ChessBoardProps {
  gameMode: 'classic' | 'hatch';
  opponent: {
    name: string;
    isBot: boolean;
    elo?: number;
  };
  timeControl?: string;
  onMove?: (from: Position, to: Position) => void;
  onResign?: () => void;
  onTakeback?: () => void;
}

export function ChessBoard({ 
  gameMode, 
  opponent, 
  timeControl = "5+0", 
  onMove, 
  onResign, 
  onTakeback 
}: ChessBoardProps) {
  const { profile } = useUserProfile();
  const [gameState, setGameState] = useState<GameState>(createInitialGameState());
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [validMoves, setValidMoves] = useState<Position[]>([]);
  const [gameHistory, setGameHistory] = useState<GameState[]>([createInitialGameState()]);
  const [gameMoves, setGameMoves] = useState<{from: Position, to: Position, promotion?: PieceType}[]>([]);
  const [gameFinished, setGameFinished] = useState(false);
  
  // Bot chat state (only for bot games)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatTone, setChatTone] = useState<ChatTone>('friendly');
  
  // Refs for cancellation
  const botMoveRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // Clock management
  const currentTurn = gameState.sideToMove;
  
  // Memoize game status to prevent re-computation in render
  const status = useMemo(() => getGameStatus(gameState), [gameState]);
  const gameStatusStr = status.status === 'ongoing' ? 'ongoing' : status.status;
  
  const { whiteTimeMs, blackTimeMs, isTimeUp, activePlayer } = useLocalClock(
    timeControl, 
    currentTurn, 
    gameStatusStr
  );

  const isPlayerTurn = gameState.sideToMove === 'white';

  // Helper function to add bot chat message
  const addBotMessage = useCallback((event: BotChatEvent, customMessage?: string) => {
    if (!opponent.isBot || !opponent.elo) return;
    
    if (!shouldBotReact(event, opponent.elo, chatTone)) return;
    
    const message = customMessage || generateBotMessage(event, opponent.elo, chatTone);
    if (!message) return;
    
    const chatMessage: ChatMessage = {
      id: `bot-${Date.now()}-${Math.random()}`,
      sender: 'bot',
      message,
      timestamp: Date.now()
    };
    
    setChatMessages(prev => [...prev, chatMessage]);
  }, [opponent.isBot, opponent.elo, chatTone]);

  // Helper function to detect if move was a capture
  const wasMoveCapture = useCallback((from: Position, to: Position, gameState: GameState): boolean => {
    const [toRow, toCol] = to;
    return gameState.board[toRow][toCol] !== null;
  }, []);

  // Helper function to handle player chat messages
  const handlePlayerMessage = useCallback((message: string) => {
    // Add player message
    const playerMessage: ChatMessage = {
      id: `player-${Date.now()}`,
      sender: 'player',
      message,
      timestamp: Date.now()
    };
    setChatMessages(prev => [...prev, playerMessage]);
    
    // Bot response after a short delay
    setTimeout(() => {
      addBotMessage('player_message');
    }, 1000 + Math.random() * 2000); // 1-3 second delay
  }, [addBotMessage]);

  const handleSquareClick = (row: number, col: number) => {
    // Don't allow moves if time is up or game is truly over (not just in check)
    if (isTimeUp || (status.status !== 'ongoing' && status.status !== 'check')) return;
    
    // Prevent moves on black pieces for the first move
    if (gameState.fullmoveNumber === 1 && gameState.sideToMove === 'white') {
      const piece = gameState.board[row][col];
      if (piece && piece.color === 'black') {
        return;
      }
    }

    if (selectedSquare) {
      const [fromRow, fromCol] = selectedSquare;
      
      // Check if trying to move to the same square
      if (fromRow === row && fromCol === col) {
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }
      
      // Validate and make the move
      const moveResult = validateMove(gameState, selectedSquare, [row, col]);
      if (moveResult.isLegal && moveResult.newState) {
        // Check for game events before updating state
        const wasCapture = wasMoveCapture(selectedSquare, [row, col], gameState);
        const newStatus = getGameStatus(moveResult.newState);
        const wasCheck = newStatus.status === 'check';
        
        // Save move to history
        const move = { from: selectedSquare, to: [row, col] as Position };
        setGameMoves(prev => [...prev, move]);
        
        // Save current state to history before making move
        setGameHistory(prev => [...prev, moveResult.newState!]);
        setGameState(moveResult.newState);
        
        // Trigger bot chat reactions after player move
        if (opponent.isBot) {
          setTimeout(() => {
            if (wasCapture) {
              addBotMessage('capture');
            } else if (wasCheck) {
              addBotMessage('check');
            } else {
              addBotMessage('player_move');
            }
          }, 500);
        }
        
        // Call the onMove callback if provided
        if (onMove) {
          onMove([fromRow, fromCol], [row, col]);
        }
      }
      
      setSelectedSquare(null);
      setValidMoves([]);
    } else {
      const piece = gameState.board[row][col];
      if (piece && piece.color === gameState.sideToMove) {
        setSelectedSquare([row, col]);
        
        // Get valid moves for this piece
        const allMoves = getAllLegalMoves(gameState);
        const pieceMoves = allMoves
          .filter(move => move.from[0] === row && move.from[1] === col)
          .map(move => move.to);
        setValidMoves(pieceMoves);
      }
    }
  };

  const handleTakeback = () => {
    if (gameHistory.length > 1) {
      const previousState = gameHistory[gameHistory.length - 2];
      setGameState(previousState);
      setGameHistory(prev => prev.slice(0, -1));
      setSelectedSquare(null);
      setValidMoves([]);
      onTakeback?.();
    }
  };

  // Bot AI - automatically make moves when it's the bot's turn
  useEffect(() => {
    // Don't make bot moves if game is truly over (not just in check)
    if (status.status !== 'ongoing' && status.status !== 'check') return;
    
    // Reset cancellation flag for new move
    botMoveRef.current.cancelled = false;
    
    if (!isPlayerTurn && opponent.isBot && opponent.elo) {
      console.log('Bot thinking...');
      // Add thinking message
      addBotMessage('thinking');
      
      // Use native engine for weak bots (≤ 1000 ELO), Stockfish for stronger ones
      const movePromise = opponent.elo <= 1000 
        ? import('@/lib/bot-ai').then(({ getBotMove }) => getBotMove(gameState, opponent.elo))
        : getStockfishPreferredMove(gameState, opponent.elo);
      
      movePromise.then(botMove => {
        // Check if this effect was cancelled
        if (botMoveRef.current.cancelled) {
          console.log('Bot move cancelled');
          return;
        }
        
        // Double-check game is still ongoing (check is still playable)
        const currentStatus = getGameStatus(gameState);
        if (currentStatus.status !== 'ongoing' && currentStatus.status !== 'check') {
          console.log('Game ended, bot move ignored');
          return;
        }
        
        if (botMove) {
          const moveResult = validateMove(gameState, botMove.from, botMove.to, botMove.promotion);
          
          if (moveResult.isLegal && moveResult.newState) {
            // Save bot move to history
            setGameMoves(prev => [...prev, botMove]);
            
            setGameHistory(prev => [...prev, moveResult.newState!]);
            setGameState(moveResult.newState);
            
            // Add bot move message
            setTimeout(() => {
              addBotMessage('bot_move');
            }, 500);
            
            const [fromRow, fromCol] = botMove.from;
            const [toRow, toCol] = botMove.to;
            onMove?.([fromRow, fromCol], [toRow, toCol]);
          }
        }
      }).catch(error => {
        console.warn('Bot move failed:', error);
      });
    } else if (!isPlayerTurn && opponent.isBot && !opponent.elo) {
      // Fallback to old random bot logic if no ELO is provided
      const timer = setTimeout(() => {
        // Check if cancelled
        if (botMoveRef.current.cancelled) return;
        
        // Check game status
        const currentStatus = getGameStatus(gameState);
        if (currentStatus.status !== 'ongoing' && currentStatus.status !== 'check') return;
        
        const legalMoves = getAllLegalMoves(gameState);
        
        if (legalMoves.length > 0) {
          // Use unified engine for fallback
          getStockfishPreferredMove(gameState, 400).then(move => {
            if (botMoveRef.current.cancelled) return;
            
            const currentStatus = getGameStatus(gameState);
            if (currentStatus.status !== 'ongoing' && currentStatus.status !== 'check') return;
            
            // Use the move from unified engine or fallback to random
            const finalMove = move || legalMoves[Math.floor(Math.random() * legalMoves.length)];
            
            const moveResult = validateMove(gameState, finalMove.from, finalMove.to, finalMove.promotion);
            
            if (moveResult.isLegal && moveResult.newState) {
              setGameMoves(prev => [...prev, finalMove]);
              setGameHistory(prev => [...prev, moveResult.newState!]);
              setGameState(moveResult.newState);
              
              const [fromRow, fromCol] = finalMove.from;
              const [toRow, toCol] = finalMove.to;
              onMove?.([fromRow, fromCol], [toRow, toCol]);
            }
          }).catch(() => {
            // Final fallback to random move
            if (botMoveRef.current.cancelled) return;
            
            const randomMove = legalMoves[Math.floor(Math.random() * legalMoves.length)];
            const moveResult = validateMove(gameState, randomMove.from, randomMove.to, randomMove.promotion);
            
            if (moveResult.isLegal && moveResult.newState) {
              setGameMoves(prev => [...prev, randomMove]);
              setGameHistory(prev => [...prev, moveResult.newState!]);
              setGameState(moveResult.newState);
              
              const [fromRow, fromCol] = randomMove.from;
              const [toRow, toCol] = randomMove.to;
              onMove?.([fromRow, fromCol], [toRow, toCol]);
            }
          });
        }
      }, 800); // 800ms delay for bot thinking
      
      return () => clearTimeout(timer);
    }
    
    // Cleanup function to cancel bot moves
    return () => {
      botMoveRef.current.cancelled = true;
    };
  }, [gameState.sideToMove, opponent.isBot, opponent.elo, onMove, isPlayerTurn, status.status, addBotMessage]);

  const isLightSquare = (row: number, col: number) => (row + col) % 2 === 0;

  // Get piece character for image mapping
  const getPieceChar = (piece: Piece): string => {
    if (!piece) return '';
    return pieceToChar(piece);
  };

  // Pure function to get game status message (no side effects)
  const getGameStatusMessage = useCallback(() => {
    if (isTimeUp) {
      const winner = whiteTimeMs <= 0 ? 'Black' : 'White';
      return `Time's up! ${winner} wins!`;
    }
    
    if (status.status !== 'ongoing') {
      return status.status === 'checkmate' ? `Checkmate! ${status.winner === 'white' ? 'White' : 'Black'} wins` :
             status.status === 'stalemate' ? 'Stalemate - Draw!' :
             status.status === 'draw' ? `Draw - ${status.reason}` :
             status.status === 'check' ? 'Check!' : '';
    }
    
    return `${gameState.sideToMove === 'white' ? 'White' : 'Black'} to move`;
  }, [isTimeUp, whiteTimeMs, status, gameState.sideToMove]);

  const saveCompletedGame = useCallback((result: string) => {
    if (gameMoves.length === 0 || gameFinished) return;
    
    const gameData = {
      id: Date.now().toString(),
      moves: gameMoves,
      opponent: opponent.name,
      result,
      timestamp: Date.now()
    };

    const existingGames = JSON.parse(localStorage.getItem('chess-games') || '[]');
    const updatedGames = [gameData, ...existingGames].slice(0, 50); // Keep last 50 games
    localStorage.setItem('chess-games', JSON.stringify(updatedGames));
    
    toast({
      title: "Game Saved",
      description: "Your game has been saved for analysis.",
    });
    
    setGameFinished(true);
  }, [gameMoves, opponent.name, gameFinished]);

  // Effect to handle game completion
  useEffect(() => {
    // Only save game on terminal states (not on check)
    const isTerminalState = status.status === 'checkmate' || status.status === 'stalemate' || status.status === 'draw';
    if ((isTerminalState || isTimeUp) && !gameFinished) {
      const result = isTimeUp 
        ? (whiteTimeMs <= 0 ? '0-1' : '1-0')
        : status.status === 'checkmate' 
          ? (status.winner === 'white' ? '1-0' : '0-1')
          : '1/2-1/2';
      
      // Delay saving to ensure all state updates are complete
      setTimeout(() => {
        saveCompletedGame(result);
      }, 100);
    }
  }, [status.status, status.winner, isTimeUp, whiteTimeMs, gameFinished, saveCompletedGame]);

  // Bot greeting effect - only for bot games
  useEffect(() => {
    if (opponent.isBot && chatMessages.length === 0) {
      setTimeout(() => {
        addBotMessage('greeting');
      }, 1000); // 1 second delay for greeting
    }
  }, [opponent.isBot, chatMessages.length, addBotMessage]);

  // Time pressure warnings
  useEffect(() => {
    if (isPlayerTurn && whiteTimeMs < 30000 && whiteTimeMs > 0) {
      // Only warn once per time threshold
      const lastMessage = chatMessages[chatMessages.length - 1];
      if (!lastMessage || !lastMessage.message.toLowerCase().includes('time')) {
        addBotMessage('low_time');
      }
    }
  }, [isPlayerTurn, whiteTimeMs, chatMessages, addBotMessage]);

  // Game end messages
  useEffect(() => {
    if (status.status === 'checkmate' || status.status === 'stalemate' || status.status === 'draw' || isTimeUp) {
      setTimeout(() => {
        if (status.status === 'checkmate') {
          const playerWon = status.winner === 'white';
          addBotMessage(playerWon ? 'defeat' : 'victory');
        } else if (isTimeUp) {
          const playerWon = blackTimeMs <= 0;
          addBotMessage(playerWon ? 'defeat' : 'victory');
        } else {
          addBotMessage('draw');
        }
      }, 1500); // Delay to let game state settle
    }
  }, [status.status, status.winner, isTimeUp, blackTimeMs, addBotMessage]);

  return (
    <div className="h-full flex bg-background">
      {/* Left Area - Player Info & Game Status - Hidden on small screens */}
      <div className="hidden lg:flex w-72 border-r border-border bg-card overflow-y-auto flex-col">
        <div className="p-4 space-y-4">
          {/* Top Player (Opponent) */}
          <div className="space-y-3">
            <PlayerInfoCard
              name={opponent.name}
              isBot={opponent.isBot}
              elo={opponent.elo}
              color="black"
              isActivePlayer={!isPlayerTurn && !isTimeUp}
              timeMs={blackTimeMs}
              isCurrentUser={false}
              isConnected={true}
              compact={true}
            />
            
            {/* Game Status in Center */}
            <Card className="p-4 text-center border border-border/50">
              {gameMode === 'hatch' && (
                <div className="mb-3 inline-flex items-center px-3 py-1 bg-gradient-to-r from-pink-600/20 via-purple-600/20 to-cyan-600/20 rounded-full border border-pink-500/30 text-xs font-medium animate-aurora-glow">
                  <span className="bg-gradient-to-r from-cyan-200 via-pink-300 to-purple-300 bg-clip-text text-transparent font-semibold">
                    🐣 Hatch Mode
                  </span>
                </div>
              )}
              <div className={`text-sm font-semibold ${
                isTimeUp || (status.status !== 'ongoing' && status.status !== 'check') 
                  ? 'text-destructive' 
                  : isPlayerTurn 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
              }`}>
                {getGameStatusMessage()}
              </div>
            </Card>
          </div>

          {/* Game Controls */}
          <Card className="p-3 border border-border/50">
            <h3 className="text-sm font-semibold mb-3 text-card-foreground">Game Controls</h3>
            <GameControls
              onTakeback={handleTakeback}
              onResign={onResign}
              canTakeback={gameHistory.length > 1}
              showMoveNavigation={false}
              compact={false}
            />
          </Card>

          {/* Move History */}
          <Card className="p-3 border border-border/50 flex-1">
            <h3 className="text-sm font-semibold mb-3 text-card-foreground">Move History</h3>
            <div className="text-xs text-muted-foreground">
              Move history will be displayed here
            </div>
          </Card>
        </div>
      </div>

      {/* Center - Chess Board */}
      <div className="flex-1 min-w-0 flex items-center lg:items-center items-start justify-center p-2 lg:p-6">
        <div className="aspect-square w-full max-w-[min(95vw,85vh)] mx-auto">
          <NativeChessBoard
            gameState={gameState}
            onSquareClick={([row, col]) => handleSquareClick(row, col)}
            highlightedSquares={validMoves}
            selectedSquare={selectedSquare}
            flipped={false}
            enableSwipeGestures={true}
            enableHapticFeedback={true}
            fillParent={false}
            compactPadding={false}
          />
        </div>
      </div>

      {/* Right Area - User & Chat - Hidden on smaller screens */}
      <div className="hidden xl:flex w-80 border-l border-border bg-card overflow-y-auto flex-col">
        <div className="p-4 space-y-4">
          {/* Bottom Player (User) */}
          <PlayerInfoCard
            name={profile?.username || "You"}
            isBot={false}
            color="white"
            isActivePlayer={isPlayerTurn && !isTimeUp}
            timeMs={whiteTimeMs}
            isCurrentUser={true}
            isConnected={true}
            compact={true}
            avatarConfig={profile?.avatar_config}
            avatarUrl={profile?.avatar_url}
          />

          {/* Bot Chat Panel */}
          {opponent.isBot && (
            <div className="flex-1">
              <BotChatPanel
                messages={chatMessages}
                onSend={handlePlayerMessage}
                tone={chatTone}
                onToneChange={setChatTone}
                botName={opponent.name}
                isVisible={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}